import React, { useRef, useCallback } from 'react';
import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';

import { useDeleteShareSubcategoryMutation } from 'api/main';
import { useDialog, useGetShareSubcategorySubscriptions } from 'utils/hooks';
import { texts } from 'utils/languages/fr';
import { useCategoryContext } from './CategoryContext';

import SubcategoryForm from './SubcategoryForm';
import { ConfirmDialog, Loader } from 'components';
import { Box, Collapse, Paper, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const iconSx = { cursor: 'pointer' };

const SubcategoryItem = ({ subcategory, onRemove, onUpdate, categoryId }) => {
    const uId = useRef(uniqueId()); // unique id used for editing state

    const {
        editingSubcategoryId,
        newEditingSubcategoryId,
        newSubcategoryForCategory,
        setEditingSubcategoryId,
        setNewEditingSubcategoryId,
        setNewSubcategoryForCategory,
    } = useCategoryContext();

    const [deleteShareSubcategory, { isLoading: deleteLoading }] =
        useDeleteShareSubcategoryMutation();

    const { open: confirmEditOpen, onClose: handleEditClose, onOpen: handleEditOpen } = useDialog();
    const { open: deleteOpen, onClose: handleDeleteClose, onOpen: handleDeleteOpen } = useDialog();

    const { subscriptions, isLoading: subscriptionsLoading } = useGetShareSubcategorySubscriptions(
        subcategory?.id ? [subcategory?.id] : []
    );

    const handleEdit = useCallback(() => {
        setNewEditingSubcategoryId(null);
        setNewSubcategoryForCategory(null);

        setEditingSubcategoryId(uId.current);
        handleEditClose();
    }, [
        setNewEditingSubcategoryId,
        setEditingSubcategoryId,
        handleEditClose,
        setNewSubcategoryForCategory,
    ]);

    const handleConfirmEdit = useCallback(() => {
        if (editingSubcategoryId || newEditingSubcategoryId || newSubcategoryForCategory) {
            handleEditOpen();
        } else {
            handleEdit();
        }
    }, [
        editingSubcategoryId,
        newEditingSubcategoryId,
        newSubcategoryForCategory,
        handleEditOpen,
        handleEdit,
    ]);

    const handleDeleteSubcategory = useCallback(() => {
        if (subcategory?.id) {
            deleteShareSubcategory(subcategory?.id);
        } else {
            onRemove();
        }
    }, [subcategory?.id, deleteShareSubcategory, onRemove]);

    const handleCancel = useCallback(() => {
        setEditingSubcategoryId(null);
        setNewEditingSubcategoryId(null);
    }, [setEditingSubcategoryId, setNewEditingSubcategoryId]);

    return (
        <Paper
            sx={theme => ({
                padding: 2,
                borderRadius: '3px',
                marginY: 1,
                width: '100%',
                border:
                    newEditingSubcategoryId === uId.current || editingSubcategoryId === uId.current
                        ? `2px solid ${theme.palette.grey[500]}`
                        : '',
                backgroundColor:
                    newEditingSubcategoryId !== uId.current && editingSubcategoryId !== uId.current
                        ? theme.palette.grey[200]
                        : 'initial',
            })}
            elevation={3}
        >
            {newEditingSubcategoryId !== uId.current && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography noWrap sx={{ flex: '1 1 50%' }}>
                        {subcategory.name}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <ConfirmDialog
                            open={deleteOpen}
                            handleConfirm={handleDeleteSubcategory}
                            handleClose={handleDeleteClose}
                            title={texts.DELETE_SUBCATEGORY}
                            loading={deleteLoading}
                            confirmText={texts.CONFIRMER}
                            cancelText={texts.CANCEL}
                        >
                            {texts.CONFIRM_DELETE_SUBCATEGORY}
                        </ConfirmDialog>
                        <ConfirmDialog
                            open={confirmEditOpen}
                            handleConfirm={handleEdit}
                            handleClose={handleEditClose}
                            title={texts.CHANGES_NOT_CONFIRMED}
                            confirmText={texts.CONFIRMER}
                            cancelText={texts.CANCEL}
                        >
                            {texts.CONFIRM_DISCARD_CHANGES}
                        </ConfirmDialog>
                        <Loader loading={subscriptionsLoading} marginTop={0} size={20}>
                            <icons.Pencil
                                sx={{ ...iconSx, '&:hover': { color: 'primary.main' } }}
                                onClick={handleConfirmEdit}
                            />
                            <icons.Delete
                                sx={{
                                    ...iconSx,
                                    color: subscriptions?.length > 0 ? 'grey.500' : 'common.black',
                                    cursor:
                                        subscriptions?.length > 0 ? 'not-allowed' : iconSx.cursor,
                                    '&:hover': {
                                        color:
                                            subscriptions?.length > 0 ? 'grey.500' : 'error.main',
                                    },
                                }}
                                onClick={
                                    subscriptions?.length === 0 || !subcategory?.id
                                        ? handleDeleteOpen
                                        : undefined
                                }
                            />
                        </Loader>
                    </Box>
                </Box>
            )}
            <Collapse
                in={newEditingSubcategoryId === uId.current || editingSubcategoryId === uId.current}
            >
                {(newEditingSubcategoryId === uId.current ||
                    editingSubcategoryId === uId.current) && (
                    <SubcategoryForm
                        subcategory={subcategory}
                        hasSubscriptions={subscriptions?.length > 0}
                        onCancel={handleCancel}
                        categoryId={categoryId}
                        onSave={onUpdate}
                    />
                )}
            </Collapse>
        </Paper>
    );
};

SubcategoryItem.propTypes = {
    subcategory: PropTypes.object,
    onRemove: PropTypes.func,
    onUpdate: PropTypes.func,
    categoryId: PropTypes.number,
};

export default SubcategoryItem;
